import type { InsightActionItemStatusDto, InsightActionRunStatusDto } from './insightAction.wire'
import type {
  MaybeUnknown,
  ResolutionReason,
  RevertBlockedReason,
  RuntimeOutcomeReason,
} from './insightActionReason'

export type InsightActionItemStatus = InsightActionItemStatusDto
export type InsightActionRunStatus = InsightActionRunStatusDto

export const TERMINAL_RUN_STATUSES: readonly InsightActionRunStatus[] = [
  'completed',
  'partially_completed',
  'failed',
]

export function isTerminalRunStatus(status: InsightActionRunStatus | undefined): boolean {
  return status !== undefined && TERMINAL_RUN_STATUSES.includes(status)
}

export interface InsightActionCounts {
  succeeded: number
  failed: number
  awaitingApproval: number
  skipped: number
}

export interface ActorRef {
  id: number
  displayName: string
}

export interface ApprovalPolicy {
  requiredRole: string
  requiresSecondPerson: boolean
  approvalRequired: boolean
  source: string
}

export type RowEligibilityState = 'eligible' | 'pending' | 'unknown' | 'blocked'

export interface RowEligibility {
  state: RowEligibilityState
  reasonCode: MaybeUnknown<ResolutionReason | RuntimeOutcomeReason>
  message: string
  retryable: boolean
  releasedAt: string | null
}

export interface InsightActionRow {
  rowKey: string
  actionable: boolean
  channelKey: string | null
  reasonCode: MaybeUnknown<ResolutionReason> | null
  campaignLabel: string | null
  eligibility: RowEligibility | null
  summary: string
  columns: Record<string, string | number | null>
}

export interface ChannelSummary {
  key: string
  total: number
  actionable: number
  blocked: number
  reason: MaybeUnknown<ResolutionReason> | null
}

export interface InsightActionGroupSummary {
  total: number
  actionable: number
  blocked: number
  rowLimitTruncated: boolean
  byChannel: ChannelSummary[]
}

export type GroupEligibilityState = 'ready' | 'partial' | 'pending'

export interface InsightActionGroup {
  key: string
  label: string
  tabKey: string
  approval: ApprovalPolicy | null
  revertible: boolean
  eligibilityState: GroupEligibilityState
  summary: InsightActionGroupSummary
  rows: InsightActionRow[]
}

export interface ActiveRun {
  runId: string
  status: InsightActionRunStatus
  totalItems: number
  counts: InsightActionCounts
}

export interface InsightActionPreview {
  insightId: number
  dataKey: string
  configEnum: string
  groups: InsightActionGroup[]
  activeRun: ActiveRun | null
  executionEnabled: boolean
  runCount: number
}

export interface ApplyInput {
  groupKey: string
  dataKey: string
  rowKeys: string[]
  idempotencyKey: string
}

export interface RunItemView {
  rowKey: string
  status: InsightActionItemStatus
  reasonCode: MaybeUnknown<ResolutionReason | RuntimeOutcomeReason>
  errorMessage: string
  id: number | null
  summary: string
}

export interface ApplyResult {
  runId: string
  status: InsightActionRunStatus
  alreadyExists: boolean
  totalItems: number
  counts: InsightActionCounts
  items: RunItemView[]
}

export interface RunListRow {
  runId: string
  insightId: number
  groupKey: string
  surface: string
  status: InsightActionRunStatus
  requestedBy: ActorRef | null
  totalItems: number
  counts: InsightActionCounts
  createdAt: string
  finishedAt: string | null
}

export interface RunHistoryPage {
  runs: RunListRow[]
  total: number
}

export interface RunDetailItem {
  id: number
  rowKey: string
  actionType: string
  platform: string
  marketingChannel: string
  targetLevel: string
  campaignId: string | null
  targetId: string | null
  status: InsightActionItemStatus
  reasonCode: MaybeUnknown<RuntimeOutcomeReason>
  errorMessage: string
  summary: string
  columns: Record<string, string | number | null>
  platformActionRequestId: string | null
  cancellable: boolean
  revertRequestId: string | null
  revertible: boolean
  revertBlockedReason: RevertBlockedReason | null
}

export interface RunDetail {
  runId: string
  insightId: number
  status: InsightActionRunStatus
  dataKey: string
  totalItems: number
  counts: InsightActionCounts
  items: RunDetailItem[]
}

export interface InsightActionOrigin {
  insightId: number
  insightTitle: string
  configLabel: string
  runId: string
  requestedBy: ActorRef | null
  rowKey: string
  rowSummary: string
}

/**
 * One platform request's place in an insight run, as the page-wide bulk lookup answers it (F5,
 * ORP-22267). Unlike `InsightActionOrigin` it carries what a run-level decision needs: the
 * `item_ids[]` the bulk endpoints take, and the run's whole awaiting count.
 */
export interface ApprovalRunOrigin {
  runId: string
  insightId: number
  insightTitle: string
  configLabel: string
  groupKey: string
  rowSummary: string
  /** The `item_ids[]` value the bulk approve/reject endpoints take, for this request. */
  itemId: number
  /** The run's whole awaiting count, which one page of the inbox may not cover. */
  pendingTotal: number
}

export interface RevertRunResult {
  runId: string
  itemsQueued: number
}

export interface RunApprovalItem {
  itemId: number
  rowKey: string
  actionType: string
  platform: string
  rowSummary: string
  requiredRole: string | null
  canApprove: boolean
}

export interface RunApprovals {
  runId: string
  requestedBy: ActorRef | null
  items: RunApprovalItem[]
}

export interface BulkRejectResultRow {
  itemId: number
  rowKey: string
  status: InsightActionItemStatus
  reasonCode: MaybeUnknown<RuntimeOutcomeReason>
}
